import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

export interface TimeValue {
  hour: number;
  minute: number;
}

@Component({
  selector: 'app-time-picker',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor, OnInit {
  @Input() minuteStep = 5;
  @Input() placeholder = '00:00';
  @Input() disabled = false;

  @Input() height: string = '56px';
  @Input() width: string = 'auto';
  @Input() customClass: string = '';

  @ViewChild('hourRef') hourRef!: ElementRef<HTMLInputElement>;
  @ViewChild('minuteRef') minuteRef!: ElementRef<HTMLInputElement>;

  hourInput = '';
  minuteInput = '';

  tempHour = 0;
  tempMinute = 0;

  isOpen = false;
  hasError = false;
  errorMessage = '';
  private isReplacing = { hour: false, minute: false };

  hours: number[] = Array.from({ length: 24 }, (_, i) => i);
  minutes: number[] = [];

  private onChange: (val: TimeValue | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.minutes = Array.from(
      { length: Math.ceil(60 / this.minuteStep) },
      (_, i) => i * this.minuteStep,
    );
  }

  writeValue(val: TimeValue | null): void {
    if (val) {
      this.hourInput = this.pad(val.hour);
      this.minuteInput = this.pad(val.minute);
    } else {
      this.hourInput = '';
      this.minuteInput = '';
    }
  }

  registerOnChange(fn: (val: TimeValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.hourRef?.nativeElement) {
      this.hourRef.nativeElement.disabled = isDisabled;
      this.minuteRef.nativeElement.disabled = isDisabled;
    }
    if (isDisabled) {
      this.isOpen = false;
    }
  }

  pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  get displayValue(): string {
    if (!this.hourInput && !this.minuteInput) return '';
    return `${this.hourInput || '00'}:${this.minuteInput || '00'}`;
  }

  openDropdown(): void {
    if (this.disabled) return;
    const h = parseInt(this.hourInput) || 0;
    const m = parseInt(this.minuteInput) || 0;
    this.tempHour = Math.min(Math.max(h, 0), 23);
    this.tempMinute = this.snapMinute(m);
    this.isOpen = true;
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.onTouched();
  }

  confirmDropdown(): void {
    this.hourInput = this.pad(this.tempHour);
    this.minuteInput = this.pad(this.tempMinute);
    this.clearError();
    this.emitValue();
    this.closeDropdown();
  }

  cancelDropdown(): void {
    this.closeDropdown();
  }

  selectHour(h: number): void {
    this.tempHour = h;
  }

  selectMinute(m: number): void {
    this.tempMinute = m;
  }

  snapMinute(m: number): number {
    return this.minutes.reduce((a, b) =>
      Math.abs(b - m) < Math.abs(a - m) ? b : a,
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('app-time-picker')) {
      this.closeDropdown();
    }
  }

  onHourFocus(): void {
    this.isReplacing.hour = true;
    this.hourRef.nativeElement.focus();
    this.hourRef.nativeElement.select();
  }

  onMinuteFocus(): void {
    this.isReplacing.minute = true;
    this.minuteRef.nativeElement.focus();
    this.minuteRef.nativeElement.select();
  }

  onHourInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value.replace(/\D/g, '');

    if (this.isReplacing.hour) {
      val = val.slice(-1);
      this.isReplacing.hour = false;
    }
    input.value = val.slice(0, 2);
    this.hourRef.nativeElement.value = val;
    this.hourInput = val;

    if (this.hourInput.length === 2) {
      this.minuteRef.nativeElement.focus();
      this.minuteRef.nativeElement.select();
    }
  }

  onMinuteInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value.replace(/\D/g, '');

    if (this.isReplacing.minute) {
      val = val.slice(-1);
      this.isReplacing.minute = false;
    }

    val = val.slice(0, 2);
    this.minuteRef.nativeElement.value = val;
    this.minuteInput = val;
  }

  onHourBlur(): void {
    const v = parseInt(this.hourInput);
    if (this.hourInput === '') return;
    if (isNaN(v) || v < 0 || v > 23) {
      this.setError('ชั่วโมงต้อง 00–23');
      this.hourInput = '';
      return;
    }
    this.hourInput = this.pad(v);
    this.clearError();
    this.emitValue();
  }

  onMinuteBlur(): void {
    const v = parseInt(this.minuteInput);
    if (this.minuteInput === '') return;
    if (isNaN(v) || v < 0 || v > 59) {
      this.setError('นาทีต้อง 00–59');
      this.minuteInput = '';
      return;
    }
    this.minuteInput = this.pad(v);
    this.clearError();
    this.emitValue();
  }

  onHourKeydown(event: KeyboardEvent): void {
    if (
      !/^\d$/.test(event.key) &&
      !['Backspace', 'Tab', 'ArrowUp', 'ArrowDown'].includes(event.key)
    ) {
      event.preventDefault();
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      let v = parseInt(this.hourInput) || 0;
      v = event.key === 'ArrowUp' ? Math.min(v + 1, 23) : Math.max(v - 1, 0);
      this.hourInput = this.pad(v);
      this.hourRef.nativeElement.value = this.hourInput;
      this.clearError();
      this.emitValue();
    }
  }

  onMinuteKeydown(event: KeyboardEvent): void {
    if (
      !/^\d$/.test(event.key) &&
      !['Backspace', 'Tab', 'ArrowUp', 'ArrowDown'].includes(event.key)
    ) {
      event.preventDefault();
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      let v = parseInt(this.minuteInput) || 0;
      v = this.snapMinute(
        event.key === 'ArrowUp'
          ? Math.min(v + this.minuteStep, 59)
          : Math.max(v - this.minuteStep, 0),
      );
      this.minuteInput = this.pad(v);
      this.minuteRef.nativeElement.value = this.minuteInput;
      this.clearError();
      this.emitValue();
    }
  }

  private emitValue(): void {
    const h = parseInt(this.hourInput);
    const m = parseInt(this.minuteInput);
    if (!isNaN(h) && !isNaN(m)) {
      this.onChange({ hour: h, minute: m });
    } else {
      this.onChange(null);
    }
  }

  private setError(msg: string): void {
    this.hasError = true;
    this.errorMessage = msg;
  }

  private clearError(): void {
    this.hasError = false;
    this.errorMessage = '';
  }

  setNow(): void {
    const now = new Date();
    this.tempHour = now.getHours();
    this.tempMinute = now.getMinutes();
    this.hourInput = this.pad(this.tempHour);
    this.minuteInput = this.pad(this.tempMinute);
    this.hourRef.nativeElement.value = this.hourInput;
    this.minuteRef.nativeElement.value = this.minuteInput;
    this.clearError();
    this.emitValue();
    this.isOpen = false;
  }
}
